import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './TransactionPage.css';
import { getTransactions, downloadTransactionsPDF } from '../../services/transactionService';
import { Transaction } from '../../types/transaction';

// Component & Icon Imports
import TransactionDetailsModal from '../../compoonents/TransactionDetailsModal/TransactionDetailsModal';
import ComingSoonAction from '../../compoonents/ComingSoonAction/ComingSoonAction';
import { FiEye } from 'react-icons/fi';
import searchIcon from '../../assets/images/searchicon.png';
import prevIcon from '../../assets/images/previcon.png';
import nextIcon from '../../assets/images/nexticon.png';
import pdfIcon from '../../assets/images/purchaseicon.png';
import csvIcon from '../../assets/images/shipment-icon.png';

type StatusFilter = 'all' | 'pending' | 'successful' | 'failed';

const TransactionPage: React.FC = () => {
  // --- STATE MANAGEMENT ---
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [activeStatus, setActiveStatus] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  // --- DATA FETCHING ---
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTransactions(
        activeStatus,
        currentPage,
        10,
        '',
        startDate,
        endDate
      );
      setTransactions(response.data?.transactions || []);
      setTotalPages(response.data?.pagination?.totalPages ?? 1);
      setTotalItems(response.data?.pagination?.totalItems ?? 0);
    } catch (err) {
      setError('Failed to load transactions.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeStatus, currentPage, startDate, endDate]);

  // Debounce search and fetch on change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTransactions();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchTransactions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeStatus, startDate, endDate]);

  const clearDateRange = () => {
    setStartDate('');
    setEndDate('');
  };

  const visibleTransactions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return transactions;
    return transactions.filter((t) => {
      const name = `${t.user?.firstName || ''} ${t.user?.lastName || ''}`.toLowerCase();
      return (
        name.includes(q) ||
        (t.transactionReference || '').toLowerCase().includes(q) ||
        (t.id || '').toLowerCase().includes(q)
      );
    });
  }, [transactions, searchTerm]);

  // --- EXPORT HANDLERS ---

  // 1. CSV Export (Client-Side Generator)
  const handleExportCSV = () => {
    if (transactions.length === 0) {
      alert("No data to export");
      return;
    }

    // Define CSV Headers
    const headers = ["Date", "Name", "Transaction ID", "Reference", "Category", "Amount", "Type", "Status"];
    
    // Map data to rows
    const rows = transactions.map(t => {
      const fullName = `${t.user?.firstName || ''} ${t.user?.lastName || ''}`.trim() || 'N/A';
      const date = t.createdAt ? new Date(t.createdAt).toLocaleDateString() : 'N/A';
      const amount = t.amountPaid || '0';
      
      return [
        `"${date}"`,
        `"${fullName}"`,
        `"${t.id || ''}"`,
        `"${t.transactionReference || ''}"`,
        `"${t.category || ''}"`,
        `"${amount}"`,
        `"${t.paymentType || ''}"`,
        `"${t.paymentStatus || ''}"`
      ].join(",");
    });

    // Create CSV content
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows].join("\n");
    
    // Trigger Download
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `transactions_${activeStatus}_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 2. PDF Export (Server-Side)
  const handleExportPDF = async () => {
    const confirm = window.confirm("Download PDF report for current selection?");
    if (!confirm) return;

    try {
      // Assuming backend has a PDF endpoint. If not, this part needs backend work.
      const blob = await downloadTransactionsPDF(activeStatus);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transactions_report_${activeStatus}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      alert("Failed to download PDF. Please check if the backend supports PDF generation.");
    }
  };

  // --- RENDER HELPERS ---
  const renderTableContent = () => {
    if (loading) {
      return (
        <tr><td colSpan={9} className="table-message" style={{textAlign:'center', padding:'20px'}}>Loading transactions...</td></tr>
      );
    }

    if (error) {
      return (
        <tr><td colSpan={9} className="table-message error" style={{textAlign:'center', padding:'20px', color:'red'}}>{error}</td></tr>
      );
    }

    if (visibleTransactions.length === 0) {
      return (
        <tr><td colSpan={9} className="table-message" style={{textAlign:'center', padding:'20px'}}>
           {searchTerm ? `No results for "${searchTerm}"` : "No transactions found."}
        </td></tr>
      );
    }

    return visibleTransactions.map(t => {
      const fullName = `${t.user?.firstName || ''} ${t.user?.lastName || ''}`.trim() || 'N/A';
      const amount = typeof t.amountPaid === 'string' || typeof t.amountPaid === 'number' 
        ? `₦${parseFloat(String(t.amountPaid)).toLocaleString()}` 
        : 'N/A';
      const date = t.createdAt ? new Date(t.createdAt).toLocaleDateString() : 'N/A';

      return (
        <tr key={t.id}>
          <td data-label="Name">{fullName}</td>
          <td data-label="Transaction ID">{t.id ? `${t.id.substring(0, 8)}...` : 'N/A'}</td>
          <td data-label="Transaction Ref">{t.transactionReference}</td>
          <td data-label="Category">{t.category}</td>
          <td data-label="Amount">{amount}</td>
          <td data-label="Date">{date}</td>
          <td data-label="Type">{t.paymentType}</td>
          <td data-label="Status">
            <span className={`status-badge status-${t.paymentStatus?.toLowerCase() || 'unknown'}`}>{t.paymentStatus}</span>
          </td>
          <td data-label="Action">
            <div className="table-action-buttons">
              <button
                type="button"
                className="table-action-btn"
                title="View details"
                aria-label="View details"
                onClick={() => setSelectedTransaction(t)}
              >
                <FiEye />
              </button>
            </div>
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      <div className="transaction-page">
        <header className="page-header">
          <h3>Transaction</h3>
          <div className="right-controls">
            {/* Export Buttons */}
            <div className="export-buttons">
              <ComingSoonAction title="List PDF export — coming soon">
                <button className="export-btn" type="button" disabled title="Coming soon">
                  PDF <img src={pdfIcon} alt="" />
                </button>
              </ComingSoonAction>
              <button className="export-btn" onClick={handleExportCSV} title="Download CSV">
                CSV <img src={csvIcon} alt="CSV"/>
              </button>
            </div>
            
            {/* Search Bar */}
            <div className="transaction-search-bar">
              <img src={searchIcon} alt="Search" />
              <input 
                type="text" 
                placeholder="Search name, ref..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
              />
            </div>
          </div>
        </header>

        {/* Status Tabs + Date Range Filter */}
        <div className="filter-row">
          <div className="filter-tabs">
            {(['all', 'successful', 'pending', 'failed'] as StatusFilter[]).map(tab => (
              <button 
                key={tab} 
                className={activeStatus === tab ? 'active' : ''} 
                onClick={() => setActiveStatus(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="date-range-filter">
            <label className="date-field">
              <span>From</span>
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
            <label className="date-field">
              <span>To</span>
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
            {(startDate || endDate) && (
              <button
                type="button"
                className="date-clear-btn"
                onClick={clearDateRange}
                title="Clear date range"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Transaction ID</th>
                <th>Transaction Ref</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Type</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {renderTableContent()}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {transactions.length > 0 && (
          <footer className="page-footer">
            <div className="pagination-info">Showing page {currentPage} of {totalPages} ({totalItems} items)</div>
            <div className="pagination-controls">
              <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}><img src={prevIcon} alt="Prev" /></button>
              <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages}><img src={nextIcon} alt="Next" /></button>
            </div>
          </footer>
        )}
      </div>

      {selectedTransaction && (
        <TransactionDetailsModal 
          isOpen={!!selectedTransaction} 
          onClose={() => setSelectedTransaction(null)} 
          transaction={selectedTransaction} 
        />
      )}
    </>
  );
};

export default TransactionPage;